import * as lodash from "lodash";
import { Injectable } from "@angular/core";
import { Headers, Http, Response, URLSearchParams } from "@angular/http";
import { Observable } from "rxjs";

import {
    BASE_CONSTANTS,
    BASE_URLS,
    GetServicesParameters,
    ITCIApplicationEndpoint,
    MasheryCredential,
    MasheryDomain,
    MasheryEndpointsBody,
    MasheryPackage,
    MasheryPlan,
    MasheryService,
    ITCIApplicationAuth,
    ITCIApplicationDeployResponse,
    ITCIApplicationEndpointUpdateRequest,
    ITCIApplication,
    TCI_ENVIRONMENT_VAR_TYPE,
    TCITenantAppType,
    ITCIApplicationDeploymentProvider, ITCIEnvVars, ITCIApplicationUpdateProvider, QUERY_PARAMS
} from "@tci/common";
import { NgTscOrchestratorService } from "../../../ng-shared/ng-services/ng-tsc-orchestrator.service";
import { TciTenantModelService } from "../../../ng-tenant/tci-tenant.model";
import { NgEnvVarsService } from "../../../ng-shared/ng-services/ng-env-vars.service";
import { NgUserService } from "../../../ng-shared/ng-services/ng-user.service";
import { BaseAppDetailSetEndpointService } from "../../../ng-app-hamburger-menu/ng-app-set-endpoint/base-app-hm-set-endpoint.service";
import { TCITenantMenuServices } from "../../../ng-tenant/tci-tenant.menu";

@Injectable()
export class BaseAppDetailEndpointsService {

    private static getMasheryBasePath(appId: string): string {
        return `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.APPS}${BASE_CONSTANTS.PUBLISH}${BASE_CONSTANTS.MASHERY}/${appId}`;
    }

    constructor(
        private envVarService: NgEnvVarsService,
        private http: Http,
        private userService: NgUserService,
        private tenantModelSvc: TciTenantModelService,
        private tscOrchestratorService: NgTscOrchestratorService,
        private setEndpointService: BaseAppDetailSetEndpointService,
        private menuService: TCITenantMenuServices
    ) {}

    public getMigrationStatus(app: ITCIApplication): boolean {
        return this.menuService.isMigrationReadytag(app.metaTags);
    }

    public getEnvVar(...envVarNames: string[]): ITCIEnvVars<any> {
        return this
            .envVarService
            .getEnvVar(...envVarNames);
    }

    public getAllEndpoints(
        appId: string,
        sandboxId: string
    ): Observable<ITCIApplicationEndpoint[]> {
        const url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.APPS}/${appId}/endpoints?sandboxId=${sandboxId}`;

        return this
            .http
            .get(url)
            .map((res: Response) => res.json() || []);
    }

    public saveEndpoint(appId: string, sandboxId: string,
                        endpoint: ITCIApplicationEndpoint, isDescription: boolean): Observable<ITCIApplicationEndpoint> {
        const url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.APPS}/${appId}/endpoints/${endpoint.id}?sandboxId=${sandboxId}`;
        const reqData: ITCIApplicationEndpointUpdateRequest = {};
        if (isDescription) {
            reqData.description = endpoint.description.trim();
        } else {
            reqData.title = endpoint.title.trim();
        }
        return this.http.put(url, reqData)
            .map((response: Response) => response.json() )
            .timeout(5000)
            .catch(error => {
                return Observable.throw(null);
            });
    }

    public publishToMashery() {

    }

    public checkMasherySubscription(app: ITCIApplication): Observable<boolean> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/login`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, null)
            .map(() => true);
    }

    public createMasheryService(
        app: ITCIApplication,
        serviceName: string,
        version: string = "1.0"
    ): Observable<MasheryService> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/services`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, {
                name: serviceName,
                version
            }, {
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                withCredentials: true
            })
            .map((res: Response) => res.json() as MasheryService);
    }

    public createMasheryPackage(app: ITCIApplication, packageName: string): Observable<MasheryPackage> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/packages`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, {
                name: packageName
            }, {
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                withCredentials: true
            })
            .map((res: Response) => res.json() as MasheryPackage);
    }

    public createMasheryPlan(
        app: ITCIApplication,
        packageId: string,
        planName: string,
        allowSelfProvKeys: boolean
    ): Observable<MasheryPlan> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/packages/${packageId}/plans`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, {
                name: planName,
                selfServiceKeyProvisioningEnabled: allowSelfProvKeys
            }, {
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                withCredentials: true
            })
            .map((res: Response) => res.json() as MasheryPlan);
    }

    public createMasheryEndpoints(app: ITCIApplication, serviceId: string, body: MasheryEndpointsBody): Observable<any> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/services/${serviceId}/endpoints`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, body, {
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                withCredentials: true
            })
            .map((res: Response) => res.json());
    }

    public createMasheryPlanEndpoints(
        app: ITCIApplication,
        packageId: string,
        serviceId: string,
        planIds: string[],
        body: MasheryEndpointsBody
    ): Observable<any> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/packages/${packageId}/services/${serviceId}/endpoints`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        const urlSearchParams: URLSearchParams = new URLSearchParams();

        lodash.forEach(planIds, (planId: string) => urlSearchParams.append("planId", planId));

        return this
            .http
            .post(url, body, {
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                search: urlSearchParams,
                withCredentials: true
            });
    }

    public getMasheryDomains(app: ITCIApplication): Observable<MasheryDomain[]> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/domains`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .get(url)
            .map((res: Response) => res.json() as MasheryDomain[]);
    }

    public getMasheryServices(app: ITCIApplication, params: GetServicesParameters): Observable<MasheryService[]> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/services`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .get(url, {
                search: this.serializeSearchParams(params)
            })
            .map((res: Response) => res.json() as MasheryService[]);
    }

    public getMasheryPackages(app: ITCIApplication): Observable<MasheryPackage[]> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/packages`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .get(url)
            .map((res: Response) => res.json() as MasheryPackage[]);
    }

    public getMasheryPlans(app: ITCIApplication, packageId: string): Observable<MasheryPlan[]> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/packages/${packageId}/plans`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .get(url)
            .map((res: Response) => res.json() as MasheryPlan[]);
    }

    public loginToMashery(app: ITCIApplication, credentials: MasheryCredential): Observable<boolean> {
        let url = `${BaseAppDetailEndpointsService.getMasheryBasePath(app.id)}/login`;
        url = this.addMigrationParam(url, this.getMigrationStatus(app));
        return this
            .http
            .post(url, credentials)
            .map(() => true);
    }

    public serializeSearchParams(searchParams: GetServicesParameters): string {
        return lodash.reduce(searchParams, (accumulator, value, key) => {
            return `${accumulator}${lodash.size(accumulator) === 0 ? "" : "&"}${key}=${value}`;
        }, "");
    }

    public getUserAuthForEndpoints(): Observable<ITCIApplicationAuth> {
        return this.userService.getAuthForFunction("APP");
    }

    public getEndpointChangeStatus() {
        return this.setEndpointService.getStatusSubject();
    }

    getAppStatus(app: ITCIApplication): ITCIApplicationDeployResponse {
        return this.getTenantDeployProvider(app.type)
                .getStatusSnapshot(app);
    }

    getTenantDeployProvider(tenantType: TCITenantAppType): ITCIApplicationDeploymentProvider {
        return this.tenantModelSvc.getTenant(tenantType)
            .getAppDeploymentProvider();
    }

    getTenantUpdateProvider(tenantType: TCITenantAppType): ITCIApplicationUpdateProvider {
        return this.tenantModelSvc.getTenant(tenantType)
            .getAppUpdateProvider();
    }

    getControlCenterUrl(): Observable<string> {
        return Observable
            .forkJoin([
                this
                    .envVarService
                    .getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.TSC_HOME_PAGE_URL)
                    .map((envVars: ITCIEnvVars<string>) => envVars[TCI_ENVIRONMENT_VAR_TYPE.TSC_HOME_PAGE_URL]),
                this
                    .tscOrchestratorService
                    .getGridDetails()
                    .map(gridDetails => gridDetails && lodash.find(gridDetails.tenants, {
                        tenantId: "category_apis"
                    }))
                    .map(tenant => tenant && lodash.find(
                        tenant.gridMenuItems,
                        menuItem => menuItem.id === "mashery_control_center"
                    ))
            ])
            .map(result => {
                const tscHomePageUrl = result[0];
                const tenantDetails = result[1];
                return tenantDetails ? `${tscHomePageUrl}/noAngularApp/launchtenant/${tenantDetails.tenantId}?encodedURL=${btoa(
                    tenantDetails.url)}` : ""; // Return empty strings for testuser env.
            });
    }

    private addMigrationParam(url: string, isMigrationReady: boolean) {
        if (isMigrationReady) {
            url += `?${QUERY_PARAMS.MIGRATION_READY}=${isMigrationReady}`;
        }
        return url;
    }
}
